import type { LoggedOnUser } from '@actschurch/ccms-sdk';

/**
 * Extended User Interface
 *
 * Extends SDK's LoggedOnUser with application-specific fields.
 * Maintains backward compatibility with legacy User interface.
 *
 * SDK LoggedOnUser fields:
 * - userId, firstName, surname, email, cellphone, avatar
 * - accessToken, refreshToken, expiresAt, tokenType
 * - rights, roles
 */
export interface ExtendedUser extends LoggedOnUser {
  // Convenience aliases for SDK fields
  id: string; // Alias for userId
  username: string; // Computed from firstName + surname or email
  displayName?: string; // Computed from firstName + surname

  // Additional fields not in SDK
  isFirstLogin: boolean;
  onboardingCompleted: boolean;
  domains: string[]; // Always includes 'demo'
  permissions: string[]; // Alias for SDK's 'rights'
  twoFactorEnabled: boolean;
  phone?: string; // Alias for cellphone
  createdAt?: string;
  lastLogin?: string;
}

/**
 * Enrich SDK User with Application-Specific Fields
 *
 * Transforms SDK's LoggedOnUser into ExtendedUser with additional fields.
 * Maps SDK terminology ('rights') to application terminology ('permissions').
 *
 * @param sdkUser - LoggedOnUser from SDK
 * @returns ExtendedUser with application-specific fields
 */
export function enrichUser(sdkUser: LoggedOnUser): ExtendedUser {
  // Compute username from available fields
  const firstName = sdkUser.firstName || '';
  const surname = sdkUser.surname || '';
  const username = firstName && surname
    ? `${firstName} ${surname}`.trim()
    : sdkUser.email?.split('@')[0] || 'User';

  const displayName = firstName && surname
    ? `${firstName} ${surname}`.trim()
    : undefined;

  return {
    ...sdkUser,
    // Aliases for SDK fields
    id: sdkUser.userId || '',
    username,
    displayName,
    phone: sdkUser.cellphone || undefined,

    // Map SDK's 'rights' to 'permissions' for backward compatibility
    permissions: sdkUser.rights || [],

    // Always include 'demo' domain (required for app functionality)
    domains: ['demo'],

    // Use sensible defaults for fields not in SDK
    isFirstLogin: false,
    onboardingCompleted: true,
    twoFactorEnabled: false,
  };
}

/**
 * Enrich User with Additional Data from Backend
 *
 * Optionally call this after enrichUser() to fetch additional fields
 * from /api/auth/me if needed for onboarding/2FA.
 *
 * @param user - ExtendedUser to enhance
 * @param additionalData - Additional data from backend
 * @returns ExtendedUser with merged data
 */
export function enrichUserWithBackendData(
  user: ExtendedUser,
  additionalData: Partial<ExtendedUser>
): ExtendedUser {
  return {
    ...user,
    ...additionalData,
    // Ensure permissions and rights stay in sync
    permissions: additionalData.permissions || user.permissions,
    rights: additionalData.permissions || user.rights,
  };
}

/**
 * Check if user has specific permission (right)
 *
 * @param user - ExtendedUser or null
 * @param permission - Permission string to check
 * @returns true if user has permission
 */
export function hasPermission(user: ExtendedUser | null, permission: string): boolean {
  if (!user) return false;
  return user.permissions?.includes(permission) ?? false;
}

/**
 * Check if user has any of the specified permissions
 *
 * @param user - ExtendedUser or null
 * @param permissions - Array of permission strings
 * @returns true if user has at least one permission
 */
export function hasAnyPermission(user: ExtendedUser | null, permissions: string[]): boolean {
  if (!user || !permissions.length) return false;
  return permissions.some((p) => hasPermission(user, p));
}

/**
 * Check if user has all of the specified permissions
 *
 * @param user - ExtendedUser or null
 * @param permissions - Array of permission strings
 * @returns true if user has all permissions
 */
export function hasAllPermissions(user: ExtendedUser | null, permissions: string[]): boolean {
  if (!user || !permissions.length) return false;
  return permissions.every((p) => hasPermission(user, p));
}

/**
 * Check if user has specific role
 *
 * @param user - ExtendedUser or null
 * @param role - Role string to check
 * @returns true if user has role
 */
export function hasRole(user: ExtendedUser | null, role: string): boolean {
  if (!user) return false;
  return user.roles?.includes(role) ?? false;
}

/**
 * Check if user has any of the specified roles
 *
 * @param user - ExtendedUser or null
 * @param roles - Array of role strings
 * @returns true if user has at least one role
 */
export function hasAnyRole(user: ExtendedUser | null, roles: string[]): boolean {
  if (!user || !roles.length) return false;
  return roles.some((r) => hasRole(user, r));
}

/**
 * Check if user has all of the specified roles
 *
 * @param user - ExtendedUser or null
 * @param roles - Array of role strings
 * @returns true if user has all roles
 */
export function hasAllRoles(user: ExtendedUser | null, roles: string[]): boolean {
  if (!user || !roles.length) return false;
  return roles.every((r) => hasRole(user, r));
}

/**
 * Check if user has access to specific domain
 *
 * @param user - ExtendedUser or null
 * @param domain - Domain string to check
 * @returns true if user has domain access
 */
export function hasDomainAccess(user: ExtendedUser | null, domain: string): boolean {
  if (!user) return false;
  return user.domains?.includes(domain) ?? false;
}

/**
 * Get user display name (prioritize displayName, fall back to username)
 *
 * @param user - ExtendedUser or null
 * @returns Display name or 'Guest'
 */
export function getUserDisplayName(user: ExtendedUser | null): string {
  if (!user) return 'Guest';
  return user.displayName || user.username || 'User';
}

/**
 * Get user initials for avatar
 *
 * @param user - ExtendedUser or null
 * @returns Initials (e.g., 'JD')
 */
export function getUserInitials(user: ExtendedUser | null): string {
  if (!user) return '?';

  const name = user.displayName || user.username || '';
  const parts = name.split(' ').filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  } else if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }

  return '?';
}
